// Share a solution with roles from the solution's menu on the Solution Edit page (solution sharing
// plan, stage 7, O22): one pop-up that moves from the share table to the confirmation to the results.
// The server applies every rule (4d/4e); the flow is in useShareFlow (shared with the creation
// wizard's last screen, O21).
import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import ShareTable from "./ShareTable";
import { ShareConfirmation, ShareResults } from "./ShareConfirmation";
import LeaveGuard from "./LeaveGuard";
import { useShareFlow } from "./useShareFlow";

export function ShareBusy({ phase, count }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", p: 3, gap: 1 }}>
      <CircularProgress />
      {phase === "sharing" && <Typography sx={{ fontSize: 14 }}>{`Sharing with ${count} ${count === 1 ? "role" : "roles"}...`}</Typography>}
    </Box>
  );
}

export default function ShareSolution({ open, onClose, serviceId, serviceName, retryNotReady }) {
  const f = useShareFlow({ serviceId, retryNotReady, active: !!open });
  const busy = f.phase === "sharing";
  const close = () => {
    if (busy) return; // nothing closes while a share runs
    f.stop();
    onClose();
  };
  return (
    <Dialog open={!!open} onClose={() => (f.phase === "confirm" ? f.cancelConfirm() : close())} maxWidth="md" fullWidth disableEscapeKeyDown={busy}>
      <LeaveGuard active={busy} />
      <DialogTitle>{`Share Solution Access: ${serviceName || "solution"}`}</DialogTitle>
      <DialogContent>
        {(f.phase === "loading" || busy) && <ShareBusy phase={f.phase} count={f.sending} />}
        {f.phase === "unavailable" && <Typography>Sharing is not available.</Typography>}
        {(f.phase === "error" || f.phase === "unknown") && <Typography color="error">{f.message}</Typography>}
        {f.phase === "choose" && (
          <ShareTable targets={f.targets} notOffered={f.notOffered} notOfferedCount={f.notOfferedCount} grantAll={f.grantAll} selected={f.selected} onSelect={f.setSelected} />
        )}
        {f.phase === "confirm" && (
          <ShareConfirmation targets={f.targets} selected={f.selected} results={f.results} confirmAgain={f.confirmAgain} grantAll={f.grantAll} serviceName={serviceName} />
        )}
        {f.phase === "result" && <ShareResults results={f.results} />}
      </DialogContent>
      <DialogActions>
        {f.phase === "loading" && <Button onClick={close}>Cancel</Button>}
        {f.phase === "choose" && (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button variant="contained" color="secondary" disabled={!f.selected.length} onClick={f.openConfirm}>
              {`Share Access with Roles (${f.selected.length})`}
            </Button>
          </>
        )}
        {f.phase === "confirm" && (
          <>
            <Button onClick={f.cancelConfirm}>Cancel</Button>
            <Button variant="contained" color="secondary" disabled={!f.selected.length} onClick={f.share}>
              Confirm
            </Button>
          </>
        )}
        {f.phase === "result" && f.retryIds.length > 0 && (
          <Button color="secondary" onClick={f.retry}>
            Retry
          </Button>
        )}
        {f.phase === "error" && (
          <Button color="secondary" onClick={f.reload}>
            Try again
          </Button>
        )}
        {(f.phase === "result" || f.phase === "unavailable" || f.phase === "error" || f.phase === "unknown") && (
          <Button variant="contained" color="secondary" onClick={close}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
